import React, { useEffect, useState, useRef, useCallback } from "react";
import {
	Container,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	CircularProgress,
	Paper,
	Box,
} from "@mui/material";
import { styled } from "@mui/system";
import { useTheme, useMediaQuery } from "@mui/material";
import { useVehicleData } from "../../DataContext";

const StyledTableContainer = styled(TableContainer)({
	borderRadius: "12px",
	overflow: "hidden",
	boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
	overflowX: "auto",
});

const StyledTableRow = styled(TableRow)({
	"&:hover": { backgroundColor: "#f0f8ff" },
});

const Alerts = ({ deviceId, chassisNumber }) => {
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(true);
	const wsRef = useRef(null);
	const { setAlerts } = useVehicleData();

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	// 1) make normalize stable
	const normalize = useCallback((json) => {
		const fmt = (ts) => (ts ? new Date(ts).toLocaleString() : "");
		const durStr = (ms) => {
			if (ms == null || ms < 0) return "";
			const s = Math.floor(ms / 1000);
			const d = Math.floor(s / 86400);
			const h = Math.floor((s % 86400) / 3600);
			const m = Math.floor((s % 3600) / 60);
			const ss = s % 60;
			if (d) return `${d}d ${h}h ${m}m`;
			if (h) return `${h}h ${m}m`;
			if (m) return `${m}m ${ss}s`;
			return `${ss}s`;
		};

		if (!Array.isArray(json?.data)) return [];
		return json.data
			.map((a) => {
				const startedMs = a.started_at
					? new Date(a.started_at).getTime()
					: null;
				const resolvedMs = a.resolved_at
					? new Date(a.resolved_at).getTime()
					: null;
				const active = !!a.active;
				return {
					alert_name: a.alert_name || a.alert_key || "",
					source: a.source || "",
					state: active ? "ACTIVE" : "RESOLVED",
					started_at_str: fmt(a.started_at),
					resolved_at_str: fmt(a.resolved_at),
					resolution_str:
						!active && startedMs != null && resolvedMs != null
							? durStr(resolvedMs - startedMs)
							: "",
					_active: active,
					_started_at: startedMs ?? -Infinity,
					_resolved_at: resolvedMs ?? -Infinity,
					_key: a.alert_name || a.alert_key || "",
					_source: a.source || "",
				};
			})
			.sort((x, y) => {
				const a = Number(y._active) - Number(x._active);
				if (a) return a;
				const b = y._resolved_at - x._resolved_at;
				if (b) return b;
				const c = y._started_at - x._started_at;
				if (c) return c;
				const d = x._key.localeCompare(y._key);
				if (d) return d;
				return x._source.localeCompare(y._source);
			});
	}, []);

	// 2) fetch effect: depend only on deviceId and normalize
	useEffect(() => {
		if (!deviceId) return;
		let cancelled = false;

		const base = process.env.REACT_APP_BACKEND_URL;
		const path = `devices/${encodeURIComponent(deviceId)}/alerts`;
		const url = new URL(path, base).toString();

		setLoading(true);
		fetch(url, {
			headers: {
				Authorization: `Bearer ${process.env.REACT_APP_BACKEND_TOKEN}`,
			},
		})
			.then((r) =>
				r.ok ? r.json() : Promise.reject(new Error(`${r.status}`))
			)
			.then((j) => {
				if (cancelled) return;
				setRows(normalize(j));
				setAlerts(j?.data ?? []);
			})
			.catch((e) => {
				if (cancelled) return;
				console.error("Fetch error:", e);
				setRows([]);
				setAlerts([]);
			})
			.finally(() => {
				if (cancelled) return;
				setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [deviceId, normalize, setAlerts]); // removed setAlerts from deps

	// 3) WS effect can also drop setRows/setAlerts from deps
	useEffect(() => {
		if (!deviceId) return;

		const wsBase = process.env.REACT_APP_BACKEND_WS_URL;
		const path = `ws/live/devices/${encodeURIComponent(deviceId)}/alerts`;
		const url = new URL(path, wsBase);

		const ws = new WebSocket(url.toString());
		wsRef.current = ws;

		ws.onmessage = (evt) => {
			try {
				const msg = JSON.parse(evt.data);
				const list = Array.isArray(msg?.data)
					? msg.data
					: Array.isArray(msg)
					? msg
					: [];
				setAlerts(list);
				setRows(normalize({ data: list }));
			} catch (e) {
				console.error("WS parse error:", e);
			}
		};

		ws.onerror = (e) => console.error("WS error:", e);
		ws.onclose = () => {
			if (wsRef.current === ws) wsRef.current = null;
		};

		return () => {
			try {
				ws.close();
			} catch {}
			if (wsRef.current === ws) wsRef.current = null;
		};
	}, [deviceId, normalize, setAlerts]);

	return (
		<Container maxWidth="lg" sx={{ mt: 4 }}>
			<Typography variant="subtitle1">
				Alerts{rows.length ? ` (${rows.length}) ` : " "}
				<Typography variant="caption">{chassisNumber}</Typography>
			</Typography>

			{loading ? (
				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
						mt: 4,
						minHeight: 300,
					}}
				>
					<CircularProgress />
				</Box>
			) : rows.length > 0 ? (
				<StyledTableContainer component={Paper}>
					<Table size={isMobile ? "small" : "medium"}>
						<TableHead sx={{ backgroundColor: "#1976d2" }}>
							<TableRow>
								<TableCell
									sx={{ color: "white", fontWeight: "bold" }}
								>
									Started at
								</TableCell>
								<TableCell
									sx={{ color: "white", fontWeight: "bold" }}
								>
									Resolved at
								</TableCell>
								<TableCell
									sx={{ color: "white", fontWeight: "bold" }}
								>
									State
								</TableCell>
								<TableCell
									sx={{ color: "white", fontWeight: "bold" }}
								>
									Resolution time
								</TableCell>
								<TableCell
									sx={{ color: "white", fontWeight: "bold" }}
								>
									Alert
								</TableCell>
								<TableCell
									sx={{ color: "white", fontWeight: "bold" }}
								>
									Source
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{rows.map((r, i) => (
								<StyledTableRow
									key={`${r._started_at}-${r._key}-${r._source}-${i}`}
								>
									<TableCell sx={{ wordBreak: "break-word" }}>
										{r.started_at_str || ""}
									</TableCell>
									<TableCell sx={{ wordBreak: "break-word" }}>
										{r.resolved_at_str ||
											(r.state === "ACTIVE" ? "" : "")}
									</TableCell>
									<TableCell
										sx={{
											fontWeight:
												r.state === "ACTIVE"
													? 700
													: 400,
											color:
												r.state === "ACTIVE"
													? "#d32f2f"
													: "inherit",
										}}
									>
										{r.state}
									</TableCell>
									<TableCell sx={{ wordBreak: "break-word" }}>
										{r.resolution_str}
									</TableCell>
									<TableCell sx={{ wordBreak: "break-word" }}>
										{r.alert_name}
									</TableCell>
									<TableCell sx={{ wordBreak: "break-word" }}>
										{r.source}
									</TableCell>
								</StyledTableRow>
							))}
						</TableBody>
					</Table>
				</StyledTableContainer>
			) : (
				<Box
					sx={{
						mt: 4,
						minHeight: 300,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						borderRadius: 2,
					}}
				>
					<Typography
						variant="body1"
						sx={{ textAlign: "center", color: "text.secondary" }}
					>
						No alerts found.
					</Typography>
				</Box>
			)}
		</Container>
	);
};

export default Alerts;
